/**
 * Ledger Entry Editor — Format Utilities
 *
 * Pure functions for numeric formatting, paste sanitization, percent/currency
 * conversion, and decimal precision preservation. No React hooks, no side
 * effects, fully testable with vitest.
 *
 * Design decisions derived from inherited app patterns:
 * - Backend stores percentages as fractions (0–1); UI displays 0–100 scale
 * - Currency amounts use US-formatting (comma thousands, dot decimals)
 * - Paste must strip `$`, commas, parentheses-wrapped negatives
 * - Decimal precision is caller-specified; never round below that threshold
 */

// ===========================================================================
// TYPES
// ===========================================================================

export interface FormatOptions {
	/** Number of decimal places (default 2). Does not force rounding above it. */
	decimalScale?: number | null;
	/** Include thousand-separator commas (default true). */
	thousandSeparator?: boolean;
	/** Prefix symbol (e.g., '$'). */
	prefix?: string;
	/** Suffix string (e.g., '%'). */
	suffix?: string;
}

// ===========================================================================
// NUMBER PARSING — robust string → number
// ===========================================================================

/**
 * Parse a raw string into a safe number. Handles empty strings, whitespace,
 * leading/trailing symbols, and returns NaN for truly unparseable content.
 */
export function parseNumber(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return NaN;
  if (typeof raw === 'number') return Number.isNaN(raw) ? NaN : raw;

  const cleaned = raw.toString().trim();
  if (cleaned === '') return NaN;

  // Strip common currency/percent markers and whitespace
  const stripped = cleaned.replace(/[$€£¥%,\s]/g, '').replace(/\s+/g, '');

  // Handle parenthesized negatives: "(1234)" → "-1234"
  const parenMatch = stripped.match(/^\((.+)\)$/);
  let valueStr: string;
  if (parenMatch) {
    valueStr = `-${parenMatch[1]}`;
  } else {
    valueStr = stripped;
  }

  const parsed = parseFloat(valueStr);
  return Number.isNaN(parsed) ? NaN : parsed;
}

// ===========================================================================
// CURRENCY FORMATTING
// ===========================================================================

/**
 * Format a number as a localized currency string using Intl.NumberFormat.
 *
 * - Empty / NaN input → returns ''
 * - Respects caller's decimalScale; defaults to 2
 * - Never rounds away digits the user actually typed when they are within
 *   the requested decimal scale
 */
export function formatCurrency(
  value: number | string | null | undefined,
  options: FormatOptions = {},
): string {
  const num = typeof value === 'string' ? parseNumber(value) : (value ?? NaN);

  if (Number.isNaN(num)) return '';

  const {
    decimalScale = 2,
    thousandSeparator = true,
    prefix = '$',
    suffix = '',
  } = options;

  // Build minimal Intl options — avoid forcing minimumFractionDigits beyond
  // what was explicitly asked so we don't zero-pad beyond selected precision.
  const intlOpts: Intl.NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: decimalScale ?? 2,
    maximumFractionDigits: decimalScale ?? 2,
    ...(thousandSeparator ? {} : { useGrouping: false }),
  };

  const formatted = new Intl.NumberFormat('en-US', intlOpts).format(Math.abs(num));
  const sign = num < 0 ? '-' : '';
  return `${sign}${prefix}${formatted}${suffix}`;
}

/**
 * Pad or truncate a decimal string to exactly `scale` characters.
 * E.g., "5" at scale 2 → "50"; "123" at scale 2 → "12".
 */
function decPadOrTruncate(dec: string, scale: number): string {
  if (dec.length >= scale) return dec.slice(0, scale);
  return dec.padEnd(scale, '0');
}

/**
 * Format a plain number with optional thousand separators and fixed decimal
 * places. Useful for non-currency numeric fields where a prefix isn't wanted.
 */
export function formatPlainNumber(
  value: number | string | null | undefined,
  options: Pick<FormatOptions, 'decimalScale' | 'thousandSeparator'> = {},
): string {
  const num = typeof value === 'string' ? parseNumber(value) : (value ?? NaN);

  if (Number.isNaN(num)) return '';

  const thousandSeparator = options.thousandSeparator ?? true;
  const decimalScale = options.decimalScale ?? 2;

  const absNumStr = String(Math.abs(num));
  const dotIndex = absNumStr.indexOf('.');
  const intPartRaw = dotIndex === -1 ? absNumStr : absNumStr.slice(0, dotIndex);
  const intPart = thousandSeparator
    ? parseInt(intPartRaw || '0', 10).toLocaleString('en-US')
    : intPartRaw || '0';

  const decPart = dotIndex === -1 ? '' : absNumStr.slice(dotIndex + 1);

  // Pad or truncate decimals to exactly match decimalScale
  const paddedDec = decPadOrTruncate(decPart, decimalScale);

  const sign = num < 0 ? '-' : '';
  // Omit decimal point when no decimals desired
  if (decimalScale === 0) return `${sign}${intPart}`;
  return `${sign}${intPart}.${paddedDec}`;
}

// ===========================================================================
// PERCENT CONVERSION
// ===========================================================================

/**
 * Convert a backend-stored fraction (0–1) to a display percentage (0–100).
 * Returns NaN when input is invalid.
 */
export function fractionToPercent(fraction: number | string | null | undefined): number {
  const num = typeof fraction === 'string' ? parseNumber(fraction) : (fraction ?? NaN);
  if (Number.isNaN(num)) return NaN;
  return num * 100;
}

/**
 * Convert a display percentage (0–100) to a backend fraction (0–1).
 * Returns NaN when input is invalid.
 */
export function percentToFraction(percent: number | string | null | undefined): number {
  const num = typeof percent === 'string' ? parseNumber(percent) : (percent ?? NaN);
  if (Number.isNaN(num)) return NaN;
  return num / 100;
}

/**
 * Format a display percentage with a trailing '%' and controlled decimals.
 * Empty / NaN → ''.
 */
export function formatPercent(
  value: number | string | null | undefined,
  decimalScale: number = 2,
): string {
  const num = typeof value === 'string' ? parseNumber(value) : (value ?? NaN);
  if (Number.isNaN(num)) return '';

  // Auto-detect: if value > 1 it's likely already a display percent (0–100),
  // otherwise treat as backend fraction (0–1) and scale up.
  const pct = num <= 1 ? num * 100 : num;
  return `${formatPlainNumber(pct, { decimalScale })}%`;
}

// ===========================================================================
// PASTE SANITIZATION
// ===========================================================================

/**
 * Sanitize a pasted clipboard string into a clean numeric string suitable
 * for an `<input>` field. Strips currency symbols, commas, percent signs,
 * spaces, and converts parenthesized negatives to minus-prefixed form.
 *
 * Examples:
 *   "$1,234.56"        → "1234.56"
 *   "(1,234)"          → "-1234"
 *   "12.5%"            → "12.5"
 *   "  $ 0.99  "       → "0.99"
 *   ""                 → ""
 *   "abc"              → "NaN"
 */
export function sanitizeForInput(raw: string): string {
  if (!raw) return '';

  let cleaned = raw.trim();

  // Detect parenthesized negatives BEFORE stripping chars
  const hasParenNegative = /^\s*\([^)]+\)\s*$/.test(cleaned);
  if (hasParenNegative) {
    cleaned = cleaned.replace(/^\s*\(|\)\s*$/g, '');
  }

  // Remove all known non-digit/non-dot/non-minus chars
  cleaned = cleaned.replace(/[^\d.\-+]/g, '');

  // Keep only the FIRST minus or plus sign
  const firstSignIdx = cleaned.search(/[-+]/);
  if (firstSignIdx !== -1) {
    cleaned =
			cleaned.slice(0, firstSignIdx + 1) + cleaned.slice(firstSignIdx + 1).replace(/[-+]/g, '');
  }

  // Keep only the FIRST decimal point
  const firstDotIdx = cleaned.indexOf('.');
  if (firstDotIdx !== -1) {
    cleaned = cleaned.slice(0, firstDotIdx + 1) + cleaned.slice(firstDotIdx + 1).replace(/\./g, '');
  }

  // Prepend minus from original paren wrap if no explicit minus remains
  if (hasParenNegative && !cleaned.startsWith('-') && !cleaned.startsWith('+')) {
    cleaned = `-${cleaned}`;
  }

  return cleaned;
}

/**
 * Full paste handler: sanitize then convert to a clean numeric string
 * preserving the original integer part length (no truncation).
 */
export function handlePaste(rawValue: string, current: string): string {
  const sanitized = sanitizeForInput(rawValue);
  // If sanitized is just "-", treat as empty until digit follows
  if (sanitized === '-') return current;
  return sanitized;
}

// ===========================================================================
// FIELD TYPE DETECTION
// ===========================================================================

/**
 * Map a field key to its input type for FieldRenderer.
 * Used by ledger entry editor modal to determine formatting/parsing behavior.
 */
export function getFieldType(
  fieldKey: string,
): 'text' | 'number' | 'currency' | 'percent' | 'date' | 'select' | 'checkbox' {
  switch (fieldKey) {
    // Currency amounts
    case 'amount':
    case 'amount_override':
    case 'linkedAmount':
    case 'associationAmount':
      return 'currency';

      // Dates
    case 'entryDate':
    case 'entry_date':
      return 'date';

      // Percentages (display 0-100)
    case 'percent':
    case 'associationPercent':
      return 'percent';

      // Numbers
    case 'number':
    case 'months':
    case 'months_advanced':
    case 'annuality':
      return 'number';

      // Checkboxes (toggle fields)
    case 'posted':
    case 'faked':
    case 'overridden':
      return 'checkbox';

      // Select fields (dropdown with allowed values from listing codes)
    case 'kind':
    case 'letter':
    case 'code':
      return 'select';

      // Default to text (code, description, partyName, partyEmail, partyKind)
    default:
      return 'text';
  }
}

// ===========================================================================
// DEBOUNCED INPUT VALUE — live typing helper
// ===========================================================================

/**
 * Given a raw keystroke event value, return a cleaned version that allows
 * normal typing: permits partial numbers like "." or "-" without breaking.
 * Useful for onKeyPress/onKeyDown validation.
 */
export function normalizeTypedValue(input: string): string {
  if (!input) return '';

  const val = input.trim();

  // Allow single dots and dashes through during typing
  if (val === '.' || val === '-' || val === '-.') return val;

  return sanitizeForInput(val);
}

// ===========================================================================
// LISTING NORMALIZATION
// ===========================================================================

/**
 * Convert listing entries to FieldRenderer select options.
 *
 * Transforms { code, description }[] into { value, label }[] suitable
 * for use with FieldRenderer type="select" component. Gracefully handles
 * empty/null inputs and missing descriptions by falling back to code.
 */
export function normalizeListingToOptions(
  entries: Array<{ code: string; description?: string | null }> | undefined | null,
): Array<{ value: string; label: string }> {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  return entries.map((entry) => ({
    value: entry.code,
    label: entry.description && entry.description.trim() ? entry.description.trim() : entry.code,
  }));
}
